













































































































import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import spacetime, {Spacetime} from "spacetime";
import {DescTypeOption, SelectOption} from "@/util/selectOption";
import {FileInfo} from "@/components/input/FileInput.vue";
import axios from 'axios';
import {BalanceEntryType, isMileageTransaction, TransactionData} from "@/util/transactionData";
import {toSentence} from "@/util/formatter";

@Component
export default class BalanceEntry extends Vue {
    @Prop()
    descriptionOptions!: DescTypeOption[];

    @Prop({
        default: () => []
    })
    descTypesUsedInCurrentTaxPeriod!: DescTypeOption[];

    @Prop()
    entryType!: BalanceEntryType;

    @Prop({
        default: null
    })
    transactionToEdit!: TransactionData | null;

    private errors: any = {
        amountError: null,
        mileageAllowanceError: null,
        noteError: null,
        dateAddedError: null,
        customDescriptionError: null,
        attachmentError: null,
        recurrenceEndError: null,
        recurrenceError: null,
        descriptionError: null
    };

    private otherError: string | null = null;

    private warnings: any = {
        amountWarning: null,
        mileageAllowanceWarning: null
    };

    private amount: number = !isMileageTransaction(this.transactionToEdit) ? this.transactionToEdit?.amount ?? 0 : 0;
    private mileageAllowance: number = this.transactionToEdit?.mileageAllowance ?? 0;

    private note: string | null = this.transactionToEdit?.note || null;
    private date: Spacetime | null = this.isEditing ? spacetime(this.transactionToEdit!.date, 'utc') : null;
    private description: DescTypeOption | null = null;
    private customDescription: string | null = this.transactionToEdit?.descriptionId === -1 ? this.transactionToEdit?.description : null;
    private saveCustomDescription: boolean = false;
    private fileAttachment: File | null = null;
    private isSubmittingForm: boolean = false;
    private deleteAttachedFile: boolean = false;
    private readonly recurrenceOptions: SelectOption[] = [
        {
            id: 0,
            name: 'None'
        },
        {
            id: 1,
            name: 'Weekly'
        },
        {
            id: 2,
            name: 'Monthly'
        }
    ];
    private recurrence: SelectOption = this.recurrenceOptions[0];
    private recurrenceTotalTimes: number = 1;
    private recurrenceEndType: 'never' | 'numTimes' = 'never';

    private isDeletingItem: boolean = false;

    private readonly otherDescTypeOption: DescTypeOption = {
        name: 'Custom…',
        id: -1,
        canRecur: true
    };

    private readonly descNamesCannotUseWithMileage = [
        'Fuel', 'Insurance', 'Road Tax', 'Repairs & Maintenance', 'Depreciation'
    ];

    private mounted() {
        if (this.transactionToEdit) {
            this.description = {
                name: this.transactionToEdit.description,
                id: this.transactionToEdit.descriptionId,
                canRecur: false
            }
        }
    }

    private get isEditing(): boolean {
        return this.transactionToEdit !== null;
    }

    private get canUploadFiles(): boolean {
        return (window as any).IS_PRO_SUBSCRIBER || false;
    }

    private get isEditingRecurringInstance(): boolean {
        return this.transactionToEdit !== null && this.transactionToEdit.recurrentParentId !== null;
    }

    private get descriptionOptionsWithOther(): DescTypeOption[] {
        return this.descriptionOptions.concat([this.otherDescTypeOption]);
    }

    private get isCustomDescription(): boolean {
        return this.description?.id === -1;
    }

    private get isMileageAllowanceCar(): boolean {
        return this.description?.id === 199 || this.description?.name === 'Mileage Allowance (Car)'
    }

    private get isMileageAllowanceMotorbike(): boolean {
        return this.description?.id === 799 || this.description?.name === 'Mileage Allowance (Motorbike)'
    }

    private get isMileageAllowance(): boolean {
        return this.isMileageAllowanceCar || this.isMileageAllowanceMotorbike
    }

    private get presentAttachment(): FileInfo | null {
        const attachment = this.transactionToEdit?.attachment;
        if (this.isEditing && attachment) {
            return {
                uri: attachment.url,
                name: attachment.name,
                type: attachment.type || 'unknown'
            }
        }

        return null;
    }

    private get canSubmitForm(): boolean {
        const hasErrors = Object.keys(this.errors).filter(key => this.errors[key] !== null).length > 0;
        const amountValid = (!this.isMileageAllowance && this.amount > 0) || (this.isMileageAllowance && this.mileageAllowance > 0);

        return !hasErrors &&
            amountValid &&
            this.date !== null &&
            (!this.isCustomDescription || this.customDescription !== null) &&
            this.description !== null &&
            !this.isDeletingItem;
    }

    private get isRecurrenceTotalTimesOutOfBound(): boolean {
        return this.recurrenceTotalTimes < 1 || this.recurrenceTotalTimes > 100;
    }

    @Watch('amount')
    private onAmountChanged(newAmount: number) {
        if (newAmount >= 1000) {
            this.warnings.amountWarning = `You've entered a very high amount. Please double check before submitting`;
        } else {
            this.warnings.amountWarning = null;
        }
    }

    @Watch('mileageAllowance')
    private onMileageAllowanceChanged(newMileage: number) {
        if (newMileage >= 1000) {
            this.warnings.mileageAllowanceWarning = `You've entered a very high amount. Please double check before submitting`;
        } else {
            this.warnings.mileageAllowanceWarning = null;
        }
    }

    @Watch('customDescription')
    private customDescUpdated(value: string | null) {
        this.errors.customDescriptionError = null;

        if (!value) {
            return;
        }

        const valueToSearchFor = value.trim().toUpperCase();
        const duplicateDescription = this.descriptionOptions.filter(option => option.name.toUpperCase() === valueToSearchFor).length > 0;
        if (duplicateDescription) {
            this.errors.customDescriptionError = 'This description already exists.';
        }
    }

    @Watch('description')
    private descriptionUpdated(option: DescTypeOption) {
        if (!this.isCustomDescription) {
            this.customDescription = null;
            this.errors.customDescriptionError = null;
        }

        this.errors.descriptionError = null;

        if (['Mileage Allowance (Car)', 'Mileage Allowance (Motorbike)'].includes(option.name)) {
            const conflictingDescTypeNames = this.descTypesUsedInCurrentTaxPeriod
                .filter(value => this.descNamesCannotUseWithMileage.includes(value.name))
                .map(value => value.name);

            if (conflictingDescTypeNames.length > 0) {
                this.errors.descriptionError = `You cannot claim Mileage Allowance when you're already claiming ${toSentence(conflictingDescTypeNames)}.`;
            }
        } else {
            const hasUsedMileageAllowance = this.descTypesUsedInCurrentTaxPeriod.filter(type => ['Mileage Allowance (Car)', 'Mileage Allowance (Motorbike)'].includes(type.name)).length !== 0;
            if (hasUsedMileageAllowance && this.descNamesCannotUseWithMileage.includes(option.name)) {
                this.errors.descriptionError = `You cannot claim ${option.name} when you're already claiming Mileage Allowance.`;
            }
        }

        this.updateRecurrenceWarning();
    }

    @Watch('note')
    private noteUpdated(value: string) {
        this.errors.noteError = null;
    }

    @Watch('date')
    private dateUpdated(date: Spacetime) {
        this.date = date;
        this.errors.dateError = null;
    }

    @Watch('recurrenceTotalTimes')
    private onRecurrenceTotalTimesUpdated() {
        if (this.recurrenceEndType !== 'numTimes') return;

        this.errors.recurrenceEndError = null;
        if (this.isRecurrenceTotalTimesOutOfBound) {
            this.errors.recurrenceEndError = 'Please pick a number between 1 and 100';
        }
    }

    @Watch('recurrenceEndType')
    private onRecurrenceEndTypeUpdated(newEndType: string) {
        if (newEndType === 'never') {
            this.errors.recurrenceEndError = null;
            if (this.isRecurrenceTotalTimesOutOfBound) {
                this.recurrenceTotalTimes = 1;
            }
        }
    }

    @Watch('recurrence')
    private onRecurrenceUpdated(newRecurrence: SelectOption) {
        this.updateRecurrenceWarning();
    }

    private fileUpdated(file: File | null) {
        this.fileAttachment = file;
        this.errors.fileError = null;

        this.deleteAttachedFile = file === null && this.isEditing;
    }

    private updateRecurrenceWarning() {
        this.errors.recurrenceError = null;
        this.errors.recurrenceEndError = null;

        // If the value is out of bounds, we want to assign the default value, because we also
        //  cleared the error.
        if (this.isRecurrenceTotalTimesOutOfBound) {
            this.recurrenceTotalTimes = 1;
        }

        if (!this.description) {
            return;
        }

        const showCannotRecurError = this.recurrence.id > 0 && !this.description?.canRecur;
        if (showCannotRecurError) {
            this.errors.recurrenceError = `${this.description?.name} entries cannot be set as recurring.`;
        }
    }

    private submitForm() {
        if (this.isSubmittingForm || !this.canSubmitForm) {
            return;
        }

        // TODO: Maybe prevent modal from closing?

        this.isSubmittingForm = true;

        // TODO: handle editing

        const date = this.date || spacetime.now();

        const formData = new FormData();
        formData.append('descriptionId', this.description!.id.toString());
        formData.append('dateAdded', date.format('iso-utc').toString());
        formData.append('entryType', this.entryType);
        formData.append('amount', this.amount.toString());
        formData.append('mileageAllowance', this.mileageAllowance.toString());
        formData.append('recurrence', this.recurrence.id.toString());
        if (this.recurrence.id > 0) {
            if (this.recurrenceEndType === 'numTimes') {
                formData.append('recurrenceEnd', this.recurrenceTotalTimes.toString());
            }
        }

        if (this.isEditing) {
            formData.append('id', this.transactionToEdit!.id.toString());
        }

        if (this.note) {
            formData.append('note', this.note);
        }

        if (this.customDescription) {
            formData.append('customDescription', this.customDescription);
            formData.append('saveCustomDescription', this.saveCustomDescription ? 'true' : 'false');
        }

        if (this.fileAttachment) {
            formData.append('attachment', this.fileAttachment);
        } else if (this.isEditing && this.deleteAttachedFile) {
            formData.append('deleteAttachedFile', this.deleteAttachedFile ? 'true' : 'false');
        }

        axios.post('/balance', formData)
            .then(result => {
                const hash = this.entryType === BalanceEntryType.INCOME ? 'income' : 'expense';
                const currentPath = window.location.pathname;
                window.location.href = `/balance#${hash}`;
                if (currentPath === '/balance') {
                    window.location.reload();
                }
            })
            .catch(error => {
                const data = error.response?.data;
                const errors = data?.errors;
                if (data?.message) {
                    this.otherError = data.message;
                }
                if (errors) {
                    Object.keys(errors).forEach(key => {
                        const errorKey = `${key}Error`;
                        if (this.errors.hasOwnProperty(errorKey)) {
                            this.errors[errorKey] = errors[key][0];
                        }
                    });
                }
            })
            .finally(() => {
                this.isSubmittingForm = false;
            });
    }

    private deleteEntry() {
        if (this.isDeletingItem) {
            return;
        }

        this.isDeletingItem = true;

        axios.delete(`/balance/${this.transactionToEdit?.id}`)
            .then(result => {
                window.location.reload();
            })
            .catch(error => {
                this.isDeletingItem = false;
                const data = error.response.data;
                if (data.message) {
                    this.otherError = data.message;
                }
            })
    }
}
